import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { legalAPI } from '@/utils/api'
import { toast } from 'sonner'
import { Loader2, Plus } from 'lucide-react'

interface NewActionModalProps {
  caseId: number
  onActionCreated?: () => void
  // Controlled mode props (optional)
  open?: boolean
  onOpenChange?: (open: boolean) => void
  defaultActionType?: string
  // Hide trigger button (for controlled mode)
  hideTrigger?: boolean
}

const ACTION_TYPE_OPTIONS = [
  // RSV
  { value: 'rsv_request_sent', label: 'RSV: Deckungsanfrage gesendet', group: 'RSV' },
  { value: 'rsv_approved', label: 'RSV: Deckungszusage erhalten', group: 'RSV' },
  { value: 'rsv_denied', label: 'RSV: Deckung abgelehnt', group: 'RSV' },
  { value: 'rsv_partial', label: 'RSV: Teildeckung', group: 'RSV' },

  // Pre-litigation
  { value: 'mahnschreiben_drafted', label: 'Mahnschreiben: Entwurf', group: 'Mahnverfahren' },
  { value: 'mahnschreiben_sent', label: 'Mahnschreiben: Versendet', group: 'Mahnverfahren' },
  { value: 'mahnschreiben_deadline', label: 'Mahnschreiben: Frist gesetzt', group: 'Mahnverfahren' },
  { value: 'defendant_response', label: 'Gegner: Antwort erhalten', group: 'Gegner' },
  { value: 'defendant_rejected', label: 'Gegner: Ablehnung', group: 'Gegner' },
  { value: 'defendant_offer', label: 'Gegner: Vergleichsangebot', group: 'Gegner' },

  // Litigation
  { value: 'klage_drafted', label: 'Klage: Entwurf', group: 'Gerichtsverfahren' },
  { value: 'klage_filed', label: 'Klage: Eingereicht', group: 'Gerichtsverfahren' },
  { value: 'klage_served', label: 'Klage: Zugestellt', group: 'Gerichtsverfahren' },
  { value: 'court_response', label: 'Gericht: Mitteilung', group: 'Gerichtsverfahren' },
  { value: 'hearing_scheduled', label: 'Gericht: Termin angesetzt', group: 'Gerichtsverfahren' },
  { value: 'hearing_moved', label: 'Gericht: Termin verschoben', group: 'Gerichtsverfahren' },
  { value: 'hearing_held', label: 'Gericht: Verhandlung durchgeführt', group: 'Gerichtsverfahren' },
  { value: 'judgment', label: 'Gericht: Urteil', group: 'Gerichtsverfahren' },

  // Resolution
  { value: 'settlement_reached', label: 'Vergleich geschlossen', group: 'Abschluss' },
  { value: 'payment_received', label: 'Zahlung eingegangen', group: 'Abschluss' },
  { value: 'case_won', label: 'Fall gewonnen', group: 'Abschluss' },
  { value: 'case_lost', label: 'Fall verloren', group: 'Abschluss' },
  { value: 'case_closed', label: 'Fall abgeschlossen', group: 'Abschluss' },

  // Client
  { value: 'client_info_requested', label: 'Mandant: Information angefordert', group: 'Mandant' },
  { value: 'client_info_received', label: 'Mandant: Information erhalten', group: 'Mandant' },
  { value: 'client_document_requested', label: 'Mandant: Dokument angefordert', group: 'Mandant' },
  { value: 'client_document_received', label: 'Mandant: Dokument erhalten', group: 'Mandant' },

  // Generic
  { value: 'note', label: 'Notiz', group: 'Sonstiges' },
]

export function NewActionModal({
  caseId,
  onActionCreated,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  defaultActionType,
  hideTrigger = false,
}: NewActionModalProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Support both controlled and uncontrolled modes
  const isControlled = controlledOpen !== undefined
  const isOpen = isControlled ? controlledOpen : internalOpen
  const setIsOpen = (value: boolean) => {
    if (isControlled) {
      controlledOnOpenChange?.(value)
    } else {
      setInternalOpen(value)
    }
  }

  // Form state
  const [actionType, setActionType] = useState(defaultActionType || '')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [actionDate, setActionDate] = useState(new Date().toISOString().slice(0, 16))
  const [deadline, setDeadline] = useState('')
  const [visibleToClient, setVisibleToClient] = useState(true)

  // Update action type when defaultActionType changes (for controlled mode)
  useEffect(() => {
    if (defaultActionType && isOpen) {
      setActionType(defaultActionType)
      // Auto-fill title
      const option = ACTION_TYPE_OPTIONS.find((opt) => opt.value === defaultActionType)
      if (option) {
        setTitle(option.label)
      }
    }
  }, [defaultActionType, isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!actionType || !title) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus')
      return
    }

    setIsSubmitting(true)
    try {
      await legalAPI.createAction(caseId, {
        action_type: actionType,
        title,
        description,
        action_date: new Date(actionDate).toISOString(),
        deadline: deadline || null,
        visible_to_client: visibleToClient,
        metadata: {},
      })

      toast.success('Aktion erstellt')
      resetForm()
      setIsOpen(false)
      onActionCreated?.()
    } catch (error) {
      console.error('Failed to create action:', error)
      toast.error('Fehler beim Erstellen der Aktion')
    } finally {
      setIsSubmitting(false)
    }
  }

  const resetForm = () => {
    setActionType('')
    setTitle('')
    setDescription('')
    setActionDate(new Date().toISOString().slice(0, 16))
    setDeadline('')
    setVisibleToClient(true)
  }

  // Auto-fill title based on action type
  const handleActionTypeChange = (value: string) => {
    setActionType(value)
    const option = ACTION_TYPE_OPTIONS.find((opt) => opt.value === value)
    if (option && !title) {
      setTitle(option.label)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button variant="outline" className="gap-2">
            <Plus className="h-4 w-4" />
            Neue Aktion
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Neue Aktion hinzufügen</DialogTitle>
          <DialogDescription>
            Fügen Sie eine neue Aktion zum Fallverlauf hinzu.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="action_type">Aktionstyp *</Label>
            <Select value={actionType} onValueChange={handleActionTypeChange}>
              <SelectTrigger id="action_type">
                <SelectValue placeholder="Aktionstyp auswählen..." />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {Object.entries(
                  ACTION_TYPE_OPTIONS.reduce((acc, opt) => {
                    if (!acc[opt.group]) acc[opt.group] = []
                    acc[opt.group].push(opt)
                    return acc
                  }, {} as Record<string, typeof ACTION_TYPE_OPTIONS>)
                ).map(([group, options]) => (
                  <div key={group}>
                    <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">
                      {group}
                    </div>
                    {options.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Titel *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Titel der Aktion"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Beschreibung</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optionale Beschreibung..."
              className="min-h-[80px]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="action_date">Datum/Zeit *</Label>
              <Input
                id="action_date"
                type="datetime-local"
                value={actionDate}
                onChange={(e) => setActionDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="deadline">Frist</Label>
              <Input
                id="deadline"
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="visible_to_client"
              checked={visibleToClient}
              onCheckedChange={(checked) => setVisibleToClient(checked === true)}
            />
            <Label htmlFor="visible_to_client" className="text-sm font-normal">
              Für Mandanten sichtbar
            </Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Erstellen
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
